import axios from "axios";
import AuthService from "@/services/auth.service";

export type EnabledModule = {
  key: string;
  name: string;
};

export type ProgramContext = {
  id: number;
  name: string;
  displayName: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  navColor?: string | null;
  supportPhone?: string | null;
  supportEmail?: string | null;
  demo: boolean;
  stediDemoMode: boolean;
  clientId: number;
  clientName: string;
  operatorId: number;
  operatorName: string;
  enabledModules: EnabledModule[];
};

export type CurrentContextUser = {
  id: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
};

export type ClientContext = {
  id: number;
  name: string;
  demo: boolean;
  stediDemoMode: boolean;
  operator: { id: number; name: string };
  enabledModules: EnabledModule[];
  programs: ProgramContext[];
};

const camelizeEnabledModules = (modules: any[] = []): EnabledModule[] =>
  modules.map((module) => ({
    key: module.key,
    name: module.name,
  }));

const camelizeProgram = (program: any, clientEnabledModules: EnabledModule[] = []): ProgramContext => {
  const programEnabledModules = camelizeEnabledModules(program.enabled_modules ?? program.enabledModules);

  return {
    id: program.id,
    name: program.name,
    displayName: program.display_name || program.displayName || program.name,
    logoUrl: program.logo_url ?? program.logoUrl,
    primaryColor: program.primary_color ?? program.primaryColor,
    accentColor: program.accent_color ?? program.accentColor,
    navColor: program.nav_color ?? program.navColor,
    supportPhone: program.support_phone ?? program.supportPhone,
    supportEmail: program.support_email ?? program.supportEmail,
    demo: program.demo,
    stediDemoMode: Boolean(program.stedi_demo_mode ?? program.stediDemoMode),
    clientId: program.client_id ?? program.clientId,
    clientName: program.client_name ?? program.clientName,
    operatorId: program.operator_id ?? program.operatorId,
    operatorName: program.operator_name ?? program.operatorName,
    enabledModules: programEnabledModules.length ? programEnabledModules : clientEnabledModules,
  };
};

export const getContexts = async (): Promise<{ currentProgramId: number; currentUser: CurrentContextUser | null; contexts: ClientContext[] }> => {
  const response = await axios.get("/api/v1/contexts", {
    headers: {
      Authorization: AuthService.getToken() || "",
    },
  }).catch((error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      AuthService.clearCache();
    }

    throw error;
  });

  const currentUser = response.data.current_user ?? response.data.currentUser ?? null;

  return {
    currentProgramId: response.data.current_program_id ?? response.data.currentProgramId,
    currentUser: currentUser
      ? {
          id: currentUser.id,
          email: currentUser.email,
          firstName: currentUser.first_name ?? currentUser.firstName,
          lastName: currentUser.last_name ?? currentUser.lastName,
          role: currentUser.role,
        }
      : null,
    contexts: (response.data.contexts || []).map((client: any) => {
      const enabledModules = camelizeEnabledModules(client.enabled_modules ?? client.enabledModules);

      return {
        id: client.id,
        name: client.name,
        demo: client.demo,
        stediDemoMode: Boolean(client.stedi_demo_mode ?? client.stediDemoMode),
        operator: client.operator,
        enabledModules,
        programs: (client.programs || []).map((program: any) => camelizeProgram(program, enabledModules)),
      };
    }),
  };
};
